import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import confetti from "canvas-confetti"
import { useSound } from "@/hooks/useSound"
import ovalPillImg from "@/public/oval-pill.png"
import spinWhooshSfx from "@/public/sounds/spin-whoosh.wav"
import reelStopSfx from "@/public/sounds/reel-stop.wav"
import winJingleSfx from "@/public/sounds/win-jingle.wav"
import bonusRevealSfx from "@/public/sounds/bonus-reveal.wav"
import sparkleLottie from "@/public/lotties/sparkle-overlay.lottie"
import confettiLottie from "@/public/lotties/confetti-celebration.lottie"
import starsGoldLottie from "@/public/lotties/stars-gold-burst.lottie"

const DOLLAR_SYMBOLS = ["$1", "$2", "$5", "$10", "$20", "$50"] as const
const BASE_REEL_SPIN_MS = 2600
const REEL_STAGGER = [0, 1200, 3000] as const
const LAST_REEL_STOP = BASE_REEL_SPIN_MS + REEL_STAGGER[2]

interface BonusSuperSlotsProps {
  amount: number
  onComplete: () => void
}

/* ── Dollar reel ─────────────────────────────────────────────────────── */

function DollarReel({
  spinning,
  finalSymbol,
  delayMs,
  onStop,
}: {
  spinning: boolean
  finalSymbol: string
  delayMs: number
  onStop?: () => void
}) {
  const [display, setDisplay] = React.useState<string>(
    DOLLAR_SYMBOLS[Math.floor(Math.random() * DOLLAR_SYMBOLS.length)]!
  )
  const [isSpinning, setIsSpinning] = React.useState(false)
  const [justLanded, setJustLanded] = React.useState(false)
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const onStopRef = React.useRef(onStop)
  onStopRef.current = onStop

  React.useEffect(() => {
    if (!spinning) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      setIsSpinning(false)
      return
    }

    setIsSpinning(true)
    setJustLanded(false)
    intervalRef.current = setInterval(() => {
      setDisplay(DOLLAR_SYMBOLS[Math.floor(Math.random() * DOLLAR_SYMBOLS.length)]!)
    }, 70)

    const stopTimer = setTimeout(() => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      setDisplay(finalSymbol)
      setIsSpinning(false)
      setJustLanded(true)
      onStopRef.current?.()
      setTimeout(() => setJustLanded(false), 500)
    }, BASE_REEL_SPIN_MS + delayMs)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      clearTimeout(stopTimer)
    }
  }, [spinning, finalSymbol, delayMs])

  return (
    <div
      className="relative flex-1 min-w-0 rounded-xl p-[2px] overflow-hidden"
      style={{
        background: justLanded
          ? "linear-gradient(180deg, #FFD700, #DAA520, #FFD700)"
          : isSpinning
            ? "linear-gradient(180deg, #8B7500, #FFD700, #5A4E00)"
            : "linear-gradient(180deg, #3A3520, #2A2515, #1A1A10)",
      }}
    >
      <div className="relative rounded-[10px] h-[118px] overflow-hidden bg-gradient-to-b from-[#1A1808] to-[#0A0A04]">
        <AnimatePresence>
          {justLanded && (
            <motion.div
              className="absolute inset-0 z-20 pointer-events-none bg-[#FFD700] rounded-[10px]"
              initial={{ opacity: 0.5 }}
              animate={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          )}
        </AnimatePresence>

        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,215,0,0.06),transparent_20%,transparent_80%,rgba(0,0,0,0.3))]" />

        <motion.div
          className="relative h-full flex items-center justify-center"
          animate={
            isSpinning
              ? { y: [0, -5, 4, -3, 0], scale: [1, 1.04, 0.97, 1.03, 1] }
              : justLanded
                ? { y: [12, -8, 4, -2, 0], scale: [0.92, 1.12, 0.96, 1.04, 1] }
                : { y: 0, scale: 1 }
          }
          transition={
            isSpinning
              ? { duration: 0.14, repeat: Infinity, ease: "linear" }
              : justLanded
                ? { duration: 0.45, ease: [0.22, 1.2, 0.36, 1], times: [0, 0.3, 0.55, 0.75, 1] }
                : { duration: 0.01 }
          }
        >
          <span
            className="text-3xl sm:text-4xl font-black"
            style={{
              fontFamily: "Bangers, cursive",
              color: isSpinning ? "#DAA520" : "#FFD700",
              filter: isSpinning
                ? "blur(1.5px) brightness(1.1)"
                : "drop-shadow(0 4px 12px rgba(255,215,0,0.4)) drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
              textShadow: isSpinning ? "none" : "0 0 20px rgba(255,215,0,0.3)",
              transition: "filter 0.05s",
            }}
          >
            {display}
          </span>
        </motion.div>

        <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-[#0A0A04]/90 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[#0A0A04]/90 to-transparent pointer-events-none" />
        <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-[48px] border-y border-[#FFD700]/10 pointer-events-none" />
      </div>
    </div>
  )
}

/* ── Main component ──────────────────────────────────────────────────── */

export function BonusSuperSlots({ amount, onComplete }: BonusSuperSlotsProps) {
  const [phase, setPhase] = React.useState<"ready" | "spinning" | "done">("ready")
  const [shaking, setShaking] = React.useState(false)
  const [reelsLanded, setReelsLanded] = React.useState(0)
  const timersRef = React.useRef<ReturnType<typeof setTimeout>[]>([])
  const containerRef = React.useRef<HTMLDivElement>(null)

  const { play: playWhoosh } = useSound(spinWhooshSfx, { volume: 0.6 })
  const { play: playReelStop } = useSound(reelStopSfx, { volume: 0.55 })
  const { play: playJingle } = useSound(winJingleSfx, { volume: 0.7 })
  const { play: playReveal } = useSound(bonusRevealSfx, { volume: 0.6 })

  const finalSymbol = `$${amount}`

  React.useEffect(() => {
    return () => { timersRef.current.forEach(clearTimeout) }
  }, [])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  const triggerShake = React.useCallback(() => {
    setShaking(true)
    const t = setTimeout(() => setShaking(false), 350)
    timersRef.current.push(t)
  }, [])

  const handleReelStop = React.useCallback((reelIndex: number) => {
    playReelStop()
    triggerShake()
    setReelsLanded((prev) => Math.max(prev, reelIndex + 1))
  }, [playReelStop, triggerShake])

  const spin = () => {
    if (phase !== "ready") return
    setPhase("spinning")
    setReelsLanded(0)
    playWhoosh()

    const doneTimer = setTimeout(() => {
      setPhase("done")
      playJingle()
      setTimeout(() => playReveal(), 600)
      confetti({
        particleCount: 120,
        spread: 75,
        origin: { y: 0.6 },
        colors: ["#FFD700", "#DAA520", "#FFA500", "#FFEC8B"],
      })
      const rain = setTimeout(() => {
        confetti({
          particleCount: 60,
          spread: 120,
          origin: { y: 0.3, x: 0.3 },
          colors: ["#FFD700", "#DAA520"],
        })
        confetti({
          particleCount: 60,
          spread: 120,
          origin: { y: 0.3, x: 0.7 },
          colors: ["#FFA500", "#FFEC8B"],
        })
      }, 800)
      timersRef.current.push(rain)
      onComplete()
    }, LAST_REEL_STOP + 1200)
    timersRef.current.push(doneTimer)
  }

  // Count-up animation
  const [countedValue, setCountedValue] = React.useState(0)
  React.useEffect(() => {
    if (phase !== "done" || amount <= 0) { setCountedValue(amount); return }
    setCountedValue(0)
    const start = performance.now()
    const duration = 1200
    let raf: number
    function tick(now: number) {
      const t = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      setCountedValue(Math.round(eased * amount))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [phase, amount])

  return (
    <div ref={containerRef} className="mt-4 rounded-2xl border-2 border-[#3A3520] bg-[#121208] p-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <img src={ovalPillImg} alt="Oval mascot" className="w-10 h-10 object-contain drop-shadow-[0_0_8px_rgba(255,215,0,0.4)]" />
          <div>
            <p
              className="text-[11px] uppercase tracking-[0.2em] text-[#FFD700] font-black"
              style={{ fontFamily: "Bangers, cursive" }}
            >
              Super Bonus
            </p>
            <h5 className="text-white font-black text-base" style={{ fontFamily: "Bangers, cursive", letterSpacing: "0.04em" }}>
              Super Bonus Slots
            </h5>
          </div>
        </div>
        <div className="rounded-xl border border-[#FFD700]/30 bg-[#FFD700]/10 px-3 py-2">
          <span className="text-[#FFD700] text-xs font-black tracking-wide" style={{ fontFamily: "Bangers, cursive" }}>
            PREMIUM
          </span>
        </div>
      </div>

      <p className="text-[#9A9ABF] text-xs mb-3">Match all 3 reels to reveal your super bonus cash amount.</p>

      {/* Slot housing */}
      <motion.div
        animate={shaking ? { x: [0, -4, 5, -3, 4, -2, 0], y: [0, 3, -4, 2, -3, 1, 0] } : { x: 0, y: 0 }}
        transition={shaking ? { duration: 0.35, ease: "easeOut" } : { duration: 0.1 }}
      >
        <div
          className="rounded-2xl p-4 relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #1A1808 0%, #0A0A04 100%)",
            boxShadow: "inset 0 2px 8px rgba(0,0,0,0.5), 0 0 0 2px #3A3520, 0 0 30px rgba(255,215,0,0.08)",
          }}
        >
          {/* Sparkle overlay during spin */}
          {phase === "spinning" && (
            <div className="absolute inset-0 z-30 pointer-events-none opacity-60">
              <DotLottieReact src={sparkleLottie} loop autoplay style={{ width: "100%", height: "100%" }} />
            </div>
          )}

          {/* Confetti overlay on win */}
          {phase === "done" && (
            <div className="absolute inset-0 z-30 pointer-events-none">
              <DotLottieReact src={confettiLottie} autoplay style={{ width: "100%", height: "100%" }} />
            </div>
          )}

          {/* Gold chase lights */}
          <div className="flex items-center justify-center gap-[6px] mb-4">
            {Array.from({ length: 15 }, (_, i) => {
              const color = i % 2 === 0 ? "#FFD700" : "#DAA520"
              const isActive = phase === "spinning"
              return (
                <motion.span
                  key={i}
                  className="block w-[9px] h-[9px] rounded-full"
                  style={{
                    background: `radial-gradient(circle at 35% 35%, ${color}, ${color}99)`,
                    boxShadow: isActive
                      ? `0 0 8px 3px ${color}88, 0 0 14px 5px ${color}33`
                      : `0 0 4px 1px ${color}22`,
                  }}
                  animate={{
                    opacity: isActive ? [0.15, 1, 0.15] : [0.3, 0.7, 0.3],
                    scale: isActive ? [0.85, 1.35, 0.85] : [0.95, 1.05, 0.95],
                  }}
                  transition={{
                    duration: isActive ? 0.18 : 1.2,
                    repeat: Infinity,
                    delay: i * (isActive ? 0.03 : 0.06),
                  }}
                />
              )
            })}
          </div>

          {/* Reels */}
          <div
            className="relative rounded-2xl p-3"
            style={{
              background: "linear-gradient(180deg, #0E0E08, #08080A)",
              boxShadow: "inset 0 2px 10px rgba(0,0,0,0.6), 0 0 0 1px #2A2515",
            }}
          >
            <div className="flex gap-2">
              <DollarReel
                spinning={phase === "spinning"}
                finalSymbol={finalSymbol}
                delayMs={REEL_STAGGER[0]!}
                onStop={() => handleReelStop(0)}
              />
              <DollarReel
                spinning={phase === "spinning"}
                finalSymbol={finalSymbol}
                delayMs={REEL_STAGGER[1]!}
                onStop={() => handleReelStop(1)}
              />
              <DollarReel
                spinning={phase === "spinning"}
                finalSymbol={finalSymbol}
                delayMs={REEL_STAGGER[2]!}
                onStop={() => handleReelStop(2)}
              />
            </div>

            {/* Winning line glow */}
            <motion.div
              className="pointer-events-none absolute inset-x-3 top-1/2 -translate-y-1/2 h-[54px] rounded-lg"
              animate={
                phase === "done"
                  ? {
                      borderColor: ["rgba(255,215,0,0.25)", "rgba(255,215,0,0.7)", "rgba(255,215,0,0.25)"],
                      boxShadow: [
                        "0 0 8px rgba(255,215,0,0.1), inset 0 0 8px rgba(255,215,0,0.05)",
                        "0 0 22px rgba(255,215,0,0.4), 0 0 44px rgba(255,215,0,0.2), inset 0 0 18px rgba(255,215,0,0.1)",
                        "0 0 8px rgba(255,215,0,0.1), inset 0 0 8px rgba(255,215,0,0.05)",
                      ],
                    }
                  : { borderColor: "rgba(255,215,0,0.08)", boxShadow: "0 0 8px rgba(255,215,0,0.04)" }
              }
              transition={phase === "done" ? { duration: 1.2, repeat: Infinity, ease: "easeInOut" } : { duration: 0.3 }}
              style={{ border: "1px solid rgba(255,215,0,0.08)" }}
            />
          </div>

          {/* Bottom chase lights */}
          <div className="flex items-center justify-center gap-[6px] mt-4">
            {Array.from({ length: 15 }, (_, i) => {
              const color = i % 2 === 0 ? "#FFD700" : "#DAA520"
              const isActive = phase === "spinning"
              return (
                <motion.span
                  key={`b-${i}`}
                  className="block w-[9px] h-[9px] rounded-full"
                  style={{
                    background: `radial-gradient(circle at 35% 35%, ${color}, ${color}99)`,
                    boxShadow: isActive
                      ? `0 0 8px 3px ${color}88, 0 0 14px 5px ${color}33`
                      : `0 0 4px 1px ${color}22`,
                  }}
                  animate={{
                    opacity: isActive ? [0.15, 1, 0.15] : [0.3, 0.7, 0.3],
                    scale: isActive ? [0.85, 1.35, 0.85] : [0.95, 1.05, 0.95],
                  }}
                  transition={{
                    duration: isActive ? 0.18 : 1.2,
                    repeat: Infinity,
                    delay: (15 - i) * (isActive ? 0.03 : 0.06),
                  }}
                />
              )
            })}
          </div>

          {/* Status text */}
          <div
            className="mt-3 text-center text-[11px] uppercase tracking-[0.2em] text-[#6A6A8A]"
            style={{ fontFamily: "Fredoka, sans-serif" }}
          >
            {phase === "ready" && "Press spin to reveal your super bonus"}
            {phase === "spinning" && (
              <span>
                Reels spinning...
                {reelsLanded > 0 && <span className="text-[#FFD700] ml-1">{reelsLanded}/3 locked</span>}
              </span>
            )}
            {phase === "done" && "Super bonus revealed!"}
          </div>
        </div>
      </motion.div>

      {/* Bottom CTA / result */}
      <div className="mt-3">
        {phase === "ready" && (
          <button
            type="button"
            onClick={spin}
            className="relative z-40 w-full px-4 py-3 rounded-xl border-2 border-[#3A3520] text-[#121208] font-black cursor-pointer"
            style={{
              background: "linear-gradient(135deg, #FFD700, #DAA520)",
              fontFamily: "Bangers, cursive",
              letterSpacing: "0.06em",
              fontSize: "1.05rem",
              boxShadow: "0 4px 20px rgba(255,215,0,0.25)",
            }}
          >
            Spin Super Slots
          </button>
        )}
        {phase === "spinning" && (
          <div
            className="w-full px-4 py-3 rounded-xl border-2 border-[#3A3520] bg-[#151508] text-center text-white font-black"
            style={{ fontFamily: "Bangers, cursive", letterSpacing: "0.04em" }}
          >
            Super slots spinning... big reveal incoming
          </div>
        )}
        {phase === "done" && (
          <div className="relative w-full px-4 py-3 rounded-xl border-2 border-[#FFD700]/30 bg-[#FFD700]/10 text-center overflow-hidden">
            <div className="absolute inset-0 z-0 pointer-events-none flex items-center justify-center">
              <div className="w-32 h-32">
                <DotLottieReact src={starsGoldLottie} autoplay style={{ width: "100%", height: "100%" }} />
              </div>
            </div>
            <div className="relative z-10">
              <p
                className="text-[#FFD700] text-xs uppercase tracking-[0.2em] font-black"
                style={{ fontFamily: "Bangers, cursive" }}
              >
                Super Bonus Cash
              </p>
              <p className="text-white font-black text-3xl mt-1" style={{ fontFamily: "Bangers, cursive" }}>
                ${countedValue}
              </p>
              <p className="text-[#9A9ABF] text-xs mt-1">Added to your bonus balance</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
